import {
  AssetDeclaredAvailability,
  AssetLimitForNetAvailability,
} from 'src/net-availability/types';
import {
  AssetNamesError,
  checkAssetNames,
} from 'src/net-availability/utils/asset-names';
import { BaseError } from 'src/types/base-error';
import { Result } from 'src/types/result';
import { getMapGroupFrom, getRemappedMap } from 'src/utils/map';
import { getErrorResult, getResult } from 'src/utils/result/utils';

export class UnknownAssetLimitAssetNameError extends BaseError {
  constructor(assetLimits: AssetLimitForNetAvailability[]) {
    super(`AssetLimit detected with unknown assetName`, {
      context: JSON.stringify({
        assetLimits,
      }),
    });
  }
}

export class UnknownAssetDeclaredAvailabilityAssetNameError extends BaseError {
  constructor(assetDeclaredAvailabilities: AssetDeclaredAvailability[]) {
    super(`AssetDeclaredAvailability detected with unknown assetName`, {
      context: JSON.stringify({
        assetDeclaredAvailabilities,
      }),
    });
  }
}

export class NoAssetLimitsError extends BaseError {
  constructor(assetName: string, assetLimits: AssetLimitForNetAvailability[]) {
    super(`No AssetLimits found.`, {
      context: JSON.stringify({
        assetName,
        assetLimits,
      }),
    });
  }
}

export class MultipleAssetLimitsError extends BaseError {
  constructor(assetName: string, assetLimits: AssetLimitForNetAvailability[]) {
    super(`Multiple AssetLimits found.`, {
      context: JSON.stringify({
        assetName,
        assetLimits,
      }),
    });
  }
}

export type CheckAssetNamesAndAssetLimitsAndAssetDeclaredAvailabilitiesError =
  | AssetNamesError
  | UnknownAssetLimitAssetNameError
  | UnknownAssetDeclaredAvailabilityAssetNameError
  | NoAssetLimitsError
  | MultipleAssetLimitsError;

export const checkAssetNamesAndAssetLimitsAndAssetDeclaredAvailabilities = ({
  assetNames,
  assetLimits,
  assetDeclaredAvailabilities,
}: {
  assetNames: string[];
  assetLimits: AssetLimitForNetAvailability[];
  assetDeclaredAvailabilities: AssetDeclaredAvailability[];
}): Result<
  CheckAssetNamesAndAssetLimitsAndAssetDeclaredAvailabilitiesError,
  {
    assetLimitByAssetName: Map<
      AssetLimitForNetAvailability['assetName'],
      AssetLimitForNetAvailability
    >;
    assetDeclaredAvailabilitiesByAssetName: Map<
      string,
      AssetDeclaredAvailability[]
    >;
  }
> =>
  checkAssetNames(assetNames).flatMap((uniqueAssetNames) => {
    const assetLimitsWithUnknownAssetNames = assetLimits.filter(
      (it) => !uniqueAssetNames.has(it.assetName),
    );
    if (assetLimitsWithUnknownAssetNames.length > 0) {
      return getErrorResult(new UnknownAssetLimitAssetNameError(assetLimits));
    }

    const assetDeclaredAvailabilitiesWithUnknownAssetNames =
      assetDeclaredAvailabilities.filter(
        (it) => !uniqueAssetNames.has(it.assetName),
      );
    if (assetDeclaredAvailabilitiesWithUnknownAssetNames.length > 0) {
      return getErrorResult(
        new UnknownAssetDeclaredAvailabilityAssetNameError(
          assetDeclaredAvailabilities,
        ),
      );
    }

    const assetLimitsByAssetName =
      getMapGroupFrom(assetLimits).byKey('assetName');

    for (const assetName of assetNames) {
      if (!assetLimitsByAssetName.has(assetName)) {
        return getErrorResult(new NoAssetLimitsError(assetName, assetLimits));
      }
    }

    const assetWithMultipleAssetLimits = Array.from(
      assetLimitsByAssetName.values(),
    ).find((it) => it.length > 1);

    if (assetWithMultipleAssetLimits) {
      const assetName = assetWithMultipleAssetLimits[0]!.assetName;

      return getErrorResult(
        new MultipleAssetLimitsError(assetName, assetLimits),
      );
    }

    const assetLimitByAssetName = getRemappedMap(
      assetLimitsByAssetName,
    ).withValues(([, limits]) => limits[0]!);

    const assetDeclaredAvailabilitiesByAssetName = getMapGroupFrom(
      assetDeclaredAvailabilities,
    ).byKey('assetName');

    return getResult({
      assetLimitByAssetName,
      assetDeclaredAvailabilitiesByAssetName,
    });
  });
